import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../lib/api';
const API_URL = API_BASE;
import { ShoppingCart, Search, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';

const hdrs = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('retailcliq_token') || sessionStorage.getItem('retailcliq_token') || ''}` });

export default function PurchaseOrderVoucherScreen({ theme }) {
  const isDark = theme === 'dark';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      let url = `${API_URL}/api/vouchers/purchase-order-vouchers?`;
      if (fromDate) url += `fromDate=${fromDate}&`;
      if (toDate) url += `toDate=${toDate}&`;
      if (statusFilter) url += `status=${statusFilter}&`;
      const r = await fetch(url, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setOrders(j.data || []);
      else setMessage({ type: 'error', text: j.error || 'Load failed' });
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    setLoading(false);
  }, [fromDate, toDate, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const base = isDark ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900';
  const card = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const inp = isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';
  const thC = isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600';

  const statusColor = (s) => {
    if (!s || s === 'Open') return 'text-blue-600 bg-blue-100';
    if (s === 'Partial') return 'text-yellow-600 bg-yellow-100';
    if (s === 'Closed' || s === 'Completed') return 'text-green-600 bg-green-100';
    if (s === 'Cancelled') return 'text-red-600 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  const totalBalance = orders.reduce((s, o) => s + parseFloat(o.Balance || 0), 0);
  const totalPaid = orders.reduce((s, o) => s + parseFloat(o.PaidAmount || 0), 0);

  return (
    <div className={`min-h-screen p-4 ${base}`} data-testid="po-voucher-screen">
      {/* Header */}
      <div className={`border rounded-lg p-4 mb-4 ${card}`}>
        <div className="flex items-center gap-2 mb-4">
          <ShoppingCart size={18} className="text-blue-500" />
          <h2 className="font-semibold text-lg">Purchase Order Voucher Details</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="erp-form-label">From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={`erp-input w-full ${inp}`} data-testid="po-voucher-from-date" />
          </div>
          <div>
            <label className="erp-form-label">To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={`erp-input w-full ${inp}`} data-testid="po-voucher-to-date" />
          </div>
          <div>
            <label className="erp-form-label">Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`erp-input w-full ${inp}`} data-testid="po-voucher-status">
              <option value="">All Statuses</option>
              <option value="Open">Open</option>
              <option value="Partial">Partial</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <button onClick={load} disabled={loading} className="erp-btn erp-btn-primary flex items-center gap-1 w-full justify-center" data-testid="po-voucher-search-btn">
              <Search size={14} /> {loading ? 'Loading…' : 'Search'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {orders.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className={`border rounded-lg p-3 ${card}`}>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Orders</div>
            <div className="text-xl font-bold">{orders.length}</div>
          </div>
          <div className={`border rounded-lg p-3 ${card}`}>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Paid</div>
            <div className="text-xl font-bold text-green-600">{totalPaid.toFixed(2)}</div>
          </div>
          <div className={`border rounded-lg p-3 ${card}`}>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Balance Due</div>
            <div className="text-xl font-bold text-orange-600">{totalBalance.toFixed(2)}</div>
          </div>
        </div>
      )}

      {/* Message */}
      {message.text && (
        <div className={`flex items-center gap-2 p-3 rounded mb-3 text-sm ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-testid="po-voucher-message">
          {message.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle size={14} />} {message.text}
        </div>
      )}

      {/* Grid */}
      <div className={`border rounded-lg overflow-hidden ${card}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={thC}>
              <th className="text-left p-2 font-medium">PO ID</th>
              <th className="text-left p-2 font-medium">PO Date</th>
              <th className="text-left p-2 font-medium">Supplier</th>
              <th className="text-right p-2 font-medium">PO Amount</th>
              <th className="text-right p-2 font-medium">Paid Amount</th>
              <th className="text-right p-2 font-medium">Balance</th>
              <th className="text-left p-2 font-medium">Status</th>
              <th className="text-left p-2 font-medium">Narration</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o, i) => (
              <tr key={o.POId || i} className={`border-t ${isDark ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-100 hover:bg-gray-50'}`} data-testid={`po-voucher-row-${i}`}>
                <td className="p-2 font-mono text-xs">{o.POId}</td>
                <td className="p-2">{o.PODateFormatted || o.PODate}</td>
                <td className="p-2">{o.SupplierName}</td>
                <td className="p-2 text-right">{parseFloat(o.TotalAmount || 0).toFixed(2)}</td>
                <td className="p-2 text-right text-green-600">{parseFloat(o.PaidAmount || 0).toFixed(2)}</td>
                <td className="p-2 text-right font-medium text-orange-600">{parseFloat(o.Balance || 0).toFixed(2)}</td>
                <td className="p-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(o.Status)}`}>{o.Status || 'Open'}</span>
                </td>
                <td className="p-2 text-xs text-gray-500 max-w-xs truncate">{o.Narration}</td>
              </tr>
            ))}
          </tbody>
          {orders.length > 0 && (
            <tfoot>
              <tr className={`border-t font-semibold ${thC}`}>
                <td colSpan={3} className="p-2 text-right">Totals:</td>
                <td className="p-2 text-right">{orders.reduce((s, o) => s + parseFloat(o.TotalAmount || 0), 0).toFixed(2)}</td>
                <td className="p-2 text-right text-green-600">{totalPaid.toFixed(2)}</td>
                <td className="p-2 text-right text-orange-600">{totalBalance.toFixed(2)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
        {orders.length === 0 && !loading && (
          <div className={`p-8 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            No purchase orders found for the selected criteria
          </div>
        )}
      </div>
    </div>
  );
}
